import { Injectable, DOCUMENT, inject } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import {
  map,
  catchError,
  of,
  switchMap,
  Observable,
  concatMap,
  withLatestFrom,
  tap,
} from 'rxjs';
import { DashboardAdapterService } from '../services/dashboard-adapter.service';
import { ApiService } from '../../shared/services/api/api.service';
import { QueryResponse } from '../../shared/services/api/response-templates/query-response';
import { CourseResponse } from '../../shared/services/api/models/course-response';
import * as actions from './actions/index';
import * as selector from './../state';
import { Router } from '@angular/router';
import { ProgressResponse } from '../../shared/services/api/models/progress-response';
import { Action, Store } from '@ngrx/store';
import { saveAs } from '../../shared/functions/download-certificate';

import { CommandResponse } from '../../shared/services/api/response-templates/command-response';
import { UnknownErrors } from '../../shared/services/api/response-templates/unknown-errors';
import { TRAINEE_ROUTER_TOKENS } from '../../trainee/trainee.routes';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationsComponent } from '../../shared/ui/elements/notifications/notifications.component';
import { RouterNavigatedAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { APP_ROUTES } from '../../app.routes';

@Injectable()
export class DashboardEffects {
  private actions$ = inject(Actions);
  private adapterService = inject(DashboardAdapterService);
  private api = inject(ApiService);
  private router = inject(Router);
  private store = inject(Store);
  private snackBar = inject(MatSnackBar);
  private document = inject<Document>(DOCUMENT);


  getTraineeProgress$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.DashboardPageActions.initApp,
        actions.QuizApiActions.quizUpdateSuccess,
        actions.DashboardPageActions.userClosedVideo
      ),
      concatMap(() =>
        this.api.getTraineeProgress().pipe(
          map((res: QueryResponse<ProgressResponse>) => {
            if (res.isSuccess) {
              const adaptedModuleProgress =
                this.adapterService.adaptTraineeProgress(res.result);

              return actions.DashboardApiActions.getTraineeProgressSuccess({
                progressResults: adaptedModuleProgress,
                userName: res.result.firstName + ' ' + res.result.lastName,
                culture: res.result.culture,
                traineeProgressId: res.result.traineeProgressId,
                canAccessTrainees: res.result.canAccessTrainees,
              });
            } else {
              return actions.DashboardApiActions.getTraineeProgressFailed({
                error: 'Trainee Progress Failed',
                culture: res.result.culture,
              });
            }
          }),
          catchError((error) => {
            return of(
              actions.DashboardApiActions.getTraineeProgressFailed({
                error: error.statusDescription,
                culture: error.result.culture,
              }),
            );
          }),
        ),
      ),
    ),
  );

  getCourseContent$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DashboardApiActions.getTraineeProgressSuccess),
      withLatestFrom(this.store.select(selector.getCulture)),
      switchMap(([, culture]) =>
        this.api.getDashboard(culture).pipe(
          map((res: QueryResponse<CourseResponse>) => {
            const adapterCourseResponse =
              this.adapterService.adaptCourseDataToModules(res.result);

            const sessionState = this.adapterService.getSessionStorage();

            return actions.DashboardApiActions.getCourseContentSuccess({
              courseModules: adapterCourseResponse.course,
              courseLevels: adapterCourseResponse.levelIDs,
              sessionStorage: sessionState,
            });
          }),
          catchError((error: { message: string }) =>
            of(
              actions.DashboardApiActions.getCourseContentFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  activityHandler$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.DashboardPageActions.activityTrackerDispatchedQuizRedirect,
        actions.QuizPageActions.quizRetakeNavigateHome,
      ),
      map((action) => {
        if (action.activity.redirect) {
          this.router.navigate([action.activity.redirect]);
        }
        return actions.QuizPageActions.quizResetProgress({
          resetQuizProgress: action.resetQuizProgress,
        });
      }),
    ),
  );

  quizCompleted$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.QuizPageActions.submitQuizProgress),
      withLatestFrom(this.store.select(selector.getCulture)),
      switchMap(([action, culture]) =>
        this.api.saveQuizProgress(action.moduleId, culture).pipe(
          map((res: CommandResponse<CourseResponse, UnknownErrors>) => {
            if (res.isSuccess) {
              this.router.navigate(['/home']);
              sessionStorage.removeItem('appSessionState');
              return actions.QuizApiActions.quizUpdateSuccess({
                moduleCompletedId: action.moduleId,
              });
            } else {
              return actions.QuizApiActions.quizUpdateFailed({
                error: `Something went wrong completing quiz - moduleId: ${action.moduleId}`,
              });
            }
          }),
          catchError((error: { message: string }) =>
            of(
              actions.QuizApiActions.quizUpdateFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  videoWatched$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DashboardPageActions.userCompletedVideo),
      withLatestFrom(
        this.store.select(selector.getCulture),
        this.store.select(selector.getCurrentVideo),
      ),
      switchMap(([, culture, currentVideo]) =>
        this.api.saveVideoProgress(currentVideo.id, culture).pipe(
          map((response) => {
            if (response.isSuccess) {
              return actions.DashboardApiActions.videoUpdateSuccess();
            } else {
              return actions.DashboardApiActions.videoUpdateFailed({
                error: `Video Progress Save - Failed id: ${currentVideo.id}`,
              });
            }
          }),
          catchError((error: { message: string }) =>
            of(
              actions.DashboardApiActions.videoUpdateFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  downloadCertificate: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.DashboardPageActions
          .activityTrackerDispatchedDownloadCertificate,
        actions.TraineePageActions.downloadCertificate,
      ),
      withLatestFrom(
        this.store.select(selector.getCulture),
        this.store.select(selector.getLevelsInfo),
      ),
      tap(() => {
        this._openSnackBar(6, 'Your certificate is downloading...');
      }),
      switchMap(([action, culture, levelsInfo]) =>
        this.api
          .downloadCertificate(action.levelId, culture, action.traineeId)
          .pipe(
            map((response) => {
              if (response) {
                const blob = new Blob([response], { type: 'application/pdf' });
                const level = levelsInfo
                  .find((x) => x.id === action.levelId)
                  ?.title.trim()
                  .replace(/ /g, '')
                  .toLowerCase()
                  ?.trim()
                  .replace(/ /g, '')
                  .toLowerCase();
                saveAs(
                  blob,
                  this.document,
                  `ud-${level}-certificate`,
                  `${new Date().toJSON().slice(0, 10)}`,
                );

                this._openSnackBar(4, 'Your certificate is ready');

                return actions.DashboardApiActions.DownloadCertificateSuccess({
                  certificateBlob: blob,
                  levelId: action.levelId,
                });
              }

              this._openSnackBar(
                6,
                'Something has gone wrong, please try again',
              );

              return actions.DashboardApiActions.DownloadCertificateFail({
                error: 'Something went wrong getting Level certificate',
              });
            }),
            catchError((error: { message: string }) =>
              of(
                actions.DashboardApiActions.DownloadCertificateFail({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  routeCheckOnModuleChanged: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DashboardPageActions.moduleChangeViaMenuRouteCheck),
      map((action) => {
        if (action.path) {
          if (
            action.path.includes(APP_ROUTES.QUIZ) ||
            action.path.includes(APP_ROUTES.COOKIES) ||
            action.path.includes(APP_ROUTES.TRAINEE)
          )
            this.router.navigate(['/home']);
        }
        return actions.DashboardPageActions.moduleChangeViaMenu({
          level: action.level,
          moduleIndex: action.moduleIndex,
        });
      }),
    ),
  );

  //***************************** TRAINEE-EFFECTS *************************************************
  createNewTrainee: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TraineePageActions.saveNewTraineeInvite),
      switchMap((action) =>
        this.api.inviteTrainee(action.traineeDetails).pipe(
          map((res) => {
            if (res.isSuccess) {
              this._openSnackBar(
                4,
                `Trainee ${action.traineeDetails.id ? 'updated' : 'added'} successfully `,
              );
              return actions.TraineeApiActions.inviteTraineeSuccess();
            } else {
              return actions.TraineeApiActions.inviteTraineeFailed({
                error: 'Failed to invite Trainee',
              });
            }
          }),
        ),
      ),
      catchError((error: { message: string }) =>
        of(
          actions.TraineeApiActions.inviteTraineeFailed({
            error: error.message,
          }),
        ),
      ),
    ),
  );

  getTraineeList: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.TraineePageActions.loadTraineeManagement,
        actions.TraineeApiActions.deleteTraineeSuccess,
        actions.TraineeApiActions.inviteTraineeSuccess,
      ),
      switchMap(() =>
        this.api.getTraineeList().pipe(
          map((res) => {
            if (res.isSuccess) {
              return actions.TraineeApiActions.getTraineeListSuccess({
                traineesOverview: res.result,
              });
            } else {
              return actions.TraineeApiActions.getTraineeListFailed({
                error: 'Failed to get Trainee List',
              });
            }
          }),
        ),
      ),
      catchError((error: { message: string }) =>
        of(
          actions.TraineeApiActions.getTraineeListFailed({
            error: error.message,
          }),
        ),
      ),
    ),
  );

  navigateBackToTraineeManagement = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          actions.TraineePageActions.backToTraineeManagement,
          actions.TraineeApiActions.inviteTraineeSuccess,
          actions.TraineeApiActions.getTraineeListSuccess,
        ),
        map(() => {
          this.router.navigate([`/trainee/${TRAINEE_ROUTER_TOKENS.MANAGE}`]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  navigateToInviteTrainee: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          actions.TraineePageActions.navigateToInviteTrainee,
          actions.TraineePageActions.editTrainee,
        ),
        withLatestFrom(
          this.store.select(selector.getCurrentTrainee),
          this.store.select(selector.getRemainingLicenses),
        ),
        tap(([, trainee, licenseCount]) => {
          if (licenseCount && licenseCount <= 0 && !trainee?.id) {
            this._openSnackBar(4, `You don't have enough licenses`);
            return;
          } else {
            this.router.navigate([`/trainee/${TRAINEE_ROUTER_TOKENS.INVITE}`]);
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  deleteTrainee: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TraineePageActions.deleteTrainee),
      switchMap((action) =>
        this.api.deleteTrainee(action.trainee).pipe(
          map((res) => {
            if (res.isSuccess) {
              this._openSnackBar(4, `Trainee deleted successfully `);
              return actions.TraineeApiActions.deleteTraineeSuccess();
            } else {
              return actions.TraineeApiActions.deleteTraineeFailed({
                error: `Failed to Delete Trainee `,
              });
            }
          }),
        ),
      ),
      catchError((error: { message: string }) =>
        of(
          actions.TraineeApiActions.deleteTraineeFailed({
            error: error.message,
          }),
        ),
      ),
    ),
  );

  resendTraineeInvite: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TraineePageActions.resendInvite),
      switchMap((action) =>
        this.api.reinvite(action.trainee).pipe(
          map((res) => {
            if (res.isSuccess) {
              this._openSnackBar(4, `Trainee invite sent `);
              return actions.TraineeApiActions.resendTraineeInviteSuccess();
            } else {
              return actions.TraineeApiActions.resendTraineeInviteFailed({
                error: 'Failed to Resend Trainee Email',
              });
            }
          }),
        ),
      ),
      catchError((error: { message: string }) =>
        of(
          actions.TraineeApiActions.resendTraineeInviteFailed({
            error: error.message,
          }),
        ),
      ),
    ),
  );

  private _openSnackBar(durationInSeconds: number, data: string) {
    this.snackBar.openFromComponent(NotificationsComponent, {
      duration: durationInSeconds * 1000,
      data,
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  //***************************** ROUTER-LISTENER *************************************************

  updateUrlOnNavigationWatch: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<RouterNavigatedAction>(ROUTER_NAVIGATION), // Listen for ROUTER_NAVIGATION actions
      map((action) => {
        const url = action.payload.event.urlAfterRedirects;
        const [path, ...children] = url
          .split('/')
          .filter((segment) => segment !== '');

        return actions.DashboardPageActions.setCurrentPath({ path, children });
      }),
    ),
  );
}
